import React from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import {
  Target,
  Landmark,
  FileText,
  PenTool,
  GraduationCap,
  Banknote,
  Plane,
  Home,
  CheckCircle2,
  ArrowRight
} from 'lucide-react';
const Admissions = () => {

  // Animation Variant
  const container = {
    hidden: { opacity: 0 },
    show: {
      opacity: 1,
      transition: {
        staggerChildren: 0.1
      }
    }
  };

  const item = {
    hidden: { opacity: 0, y: 20 },
    show: { opacity: 1, y: 0 }
  };

  const services = [
    {
      step: "01",
      title: 'Career Guidance & Course Selection',
      description: 'Choosing the right career path is the foundation of a successful future. At 6th Sense Academy, our certified counselors provide personalized career counseling sessions to help students identify their strengths, interests and academic goals. We use psychometric analysis, one-on-one discussions, and global education insights to guide students toward the most suitable course and country. Whether it\'s a career in Business, technology or Design, we ensure that every student makes an informed decision that aligns with both passion and opportunity.',
      icon: Target,
    },
    {
      step: "02",
      title: 'Shortlisting Universities',
      description: 'Selecting the right university can be overwhelming, given the vast number of options worldwide. Our experts carefully shortlist universities based on: Academic background and eligibility, Course specialization and curriculum quality, QS and Times Higher Education rankings, Budget and living costs, Location preferences and career prospects. We help students choose universities where they have maximum chances of admission and scholarships, ensuring both academic and financial satisfaction.',
      icon: Landmark,
    },
    {
      step: "03",
      title: 'Application & Admission Process',
      description: 'Our team simplifies the complex admission process by offering step-by-step assistance from start to finish. We help students with: Creating and managing university application portals, Document preparation and verification, Meeting application deadlines, Tracking application status and follow-ups with universities. We ensure that every detail is accurate and professional, resulting in a smooth and timely admission confirmation from the chosen universities.',
      icon: FileText,
    },
    {
      step: "04",
      title: 'Guidance on Letter of Recommendation (LOR) Preparation',
      description: 'A well-written LOR can strongly influence the admission decision. Our counselors provide guidance on choosing the right recommenders and structuring effective recommendation letters that highlight academic achievements, skills, and character traits. We ensure the LOR reflects authenticity, professionalism, and credibility, making your profile stand out to the admission committee.',
      icon: CheckCircle2,
    },
    {
      step: "05",
      title: 'Statement of Purpose (SOP) Preparation',
      description: 'Your SOP is your voice in the admission process, it tells your story. At 6th Sense Academy, we specialize in crafting personalized, impactful, and university-specific SOPs that capture your motivation, goals, academic journey, and reasons for course selection. Our SOP experts ensure that the document meets the standards of top global universities and differentiates you from thousands of other applicants.',
      icon: PenTool,
    },
    {
      step: "06",
      title: 'Scholarship Guidance',
      description: 'We believe finances should never stop a dream. Our scholarship experts guide students to explore and apply for various university-based, government, and external scholarships. We provide detailed insights into eligibility, deadlines, and application strategies for: Merit-based scholarships, Need-based grants, Country-specific scholarships, Tuition fee waivers and research funding. Our goal is to help every student maximize financial aid opportunities and minimize the cost of studying abroad.',
      icon: GraduationCap,
    },
    {
      step: "07",
      title: 'Loan Assistance',
      description: 'Financing higher education abroad can be challenging, but we make it simple. 6th Sense Academy has official tie-ups with leading education loan providers such as Avanse, HDFC Credila, Axis Bank, and several public sector banks. Our finance team assists in: Comparing loan options and interest rates, Preparing required documentation, Coordinating directly with the bank representatives, Ensuring timely loan approval and disbursement. We make sure students receive secure, affordable, and hassle-free financial support for their international education journey.',
      icon: Banknote,
    },
    {
      step: "08",
      title: 'Visa Guidance',
      description: 'Getting a visa approved is a crucial step in the process, and our visa experts have an excellent track record of success. We provide: Step-by-step guidance on visa application procedures, Assistance with documentation and financial proofs, Personalized mock interviews and preparation sessions, Updated country-specific visa regulations. Our team ensures every student approaches their visa interview with confidence and clarity, resulting in a smooth approval process.',
      icon: Plane,
    },
    {
      step: "09",
      title: 'Accommodation & Travel Assistance',
      description: 'Moving abroad can feel overwhelming, we make it easier. Our support team helps students find safe, comfortable, and affordable accommodation options near their university before departure. We also assist with: Temporary stay arrangements, Airport pickup services, Travel insurance and flight booking, Pre-departure orientation sessions. We ensure that every student is well-prepared and supported from the moment they leave home until they settle abroad comfortably.',
      icon: Home,
    },
  ];

  return (
    <div className="bg-white min-h-screen">
      
      
      {/* =========================================
          HERO SECTION
      ========================================= */}
      <section className="relative pt-32 pb-20 bg-slate-50 overflow-hidden border-b border-slate-200">
        <div className="absolute top-0 right-0 w-1/2 h-full bg-red-50/30 skew-x-12 translate-x-20 pointer-events-none"></div>
        
        <div className="max-w-7xl mx-auto px-6 relative z-10 text-center">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
          >
            <span className="text-red-600 font-bold tracking-widest uppercase text-xs mb-4 block">
              The 6th Sense Roadmap
            </span>
            <h1 className="text-4xl md:text-6xl font-serif font-bold text-slate-900 mb-6 leading-tight">
              Your Journey to {" "}
              <span className="italic text-slate-700">Global Excellence.</span>
            </h1>
            <p className="text-lg md:text-xl text-slate-600 max-w-2xl mx-auto">
              We offer complete end-to-end study abroad solutions for students aspiring to pursue their higher education across the globe.
            </p>
          </motion.div>
        </div>
      </section>

      

      {/* =========================================
          SERVICES GRID (BENTO STYLE)
      ========================================= */}
      <section className="py-24 bg-white">
        <div className="max-w-7xl mx-auto px-6">
          
          <motion.div 
            variants={container}
            initial="hidden"
            whileInView="show"
            viewport={{ once: true }}
            className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8"
          >
            {services.map((service, index) => {
              const IconComponent = service.icon;
              return (
                <motion.div
                  key={index}
                  variants={item}
                  className="group relative p-8 rounded-2xl bg-white border border-slate-100 hover:border-red-100 shadow-sm hover:shadow-2xl hover:shadow-red-900/5 transition-all duration-300 flex flex-col"
                >
                  {/* Step Number Background */}
                  <div className="absolute top-4 right-6 text-6xl font-serif font-bold text-gray-300 group-hover:text-red-400 transition-colors pointer-events-none select-none">
                    {service.step}
                  </div>

                  {/* Icon */}
                  <div className="w-14 h-14 bg-slate-50 rounded-xl flex items-center justify-center text-slate-700 mb-6 group-hover:bg-red-600 group-hover:text-white transition-colors duration-300 relative z-10">
                    <IconComponent size={28} strokeWidth={1.5} />
                  </div>

                  {/* Content */}
                  <div className="relative z-10">
                    <h3 className="text-center text-2xl font-serif font-bold text-slate-900 mb-4 group-hover:text-red-700 transition-colors">
                      {service.title}
                    </h3>
                    <p className="text-slate-600 leading-relaxed text-sm text-justify">
                      {service.description}
                    </p>
                  </div>
                </motion.div>
              );
            })}
          </motion.div>
        </div>
      </section>

      {/* =========================================
          CTA SECTION
      ========================================= */}
      <section className="py-24 bg-slate-900 text-white overflow-hidden relative">
        {/* Abstract shapes */}
        <div className="absolute top-0 right-0 w-96 h-96 bg-red-600/10 rounded-full blur-3xl -translate-y-1/2 translate-x-1/2"></div>
        <div className="absolute bottom-0 left-0 w-64 h-64 bg-blue-600/10 rounded-full blur-3xl translate-y-1/2 -translate-x-1/2"></div>

        <div className="max-w-4xl mx-auto px-6 text-center relative z-10">
          <h2 className="text-3xl text-gray-100 font-serif font-bold mb-6">
            Don't navigate this alone.
          </h2>
          <p className="text-slate-300 text-lg mb-10 leading-relaxed">
            A single error in your SOP or Visa documentation can cost you a year. 
            Let our experts handle the details while you focus on your future.
          </p>
          <Link
            to="/book-free-counselling"
            className="inline-flex items-center gap-3 bg-red-600 hover:bg-red-700 text-white px-10 py-5 rounded-lg text-lg font-bold transition-all hover:scale-105 shadow-xl shadow-red-900/20"
          >
            Start Your Free Assessment
            <ArrowRight size={20} />
          </Link>
        </div>
      </section>
    </div>
  );
};

export default Admissions;